import { Button, DialogActions, DialogContent } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useRef } from "react";
import { useStoreon } from "storeon/react";
import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { attributeTypeBindService } from "@services/Settings";
import { TableEvents, TableState } from "@store/table-module";
import { useAsync, useLoading } from "@hooks";
import { ModalContentLoading } from "@components/Common/Modal";
import { CreateAttributeTypeBindDto, UpdateAttributeTypeBindDto } from "@dto/AttributeTypeBindDto";
import { AttributeTypeBindForm } from "@components/Forms/Settings";

const AttributeTypeBindDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const { itemId } = modalProps;

    const formRef = useRef<any>();

    const { enqueueSnackbar } = useSnackbar();
    const { dispatch } = useStoreon<TableState, TableEvents>();
    const { loadingStatus: submitLoadingStatus, setLoadingStatus: setSubmitLoadingStatus } =
        useLoading("inactivity");

    const { data, loadingStatus } = useAsync(attributeTypeBindService.getOneATypeBind, {
        functionArgs: [itemId],
        canLoad: !!itemId,
    });

    const invokeSubmit = () => {
        formRef.current.handleSubmit();
    };

    const handleCreateAttributeTypeBind = async (values) => {
        setSubmitLoadingStatus("loading");
        try {
            const dto = attributeTypeBindService.plainToClass(CreateAttributeTypeBindDto, values);
            await attributeTypeBindService.createATypeBind(dto);
            enqueueSnackbar("Привязка типа атрибута успешно создана", {
                variant: "success",
            });
            onClose();
            dispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при создании привязки типа атрибута", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    const handleUpdateAttributeTypeBind = async (values) => {
        if (!itemId) return;

        setSubmitLoadingStatus("loading");
        try {
            const dto = attributeTypeBindService.plainToClass(UpdateAttributeTypeBindDto, values);
            await attributeTypeBindService.updateATypeBind(itemId, dto);

            enqueueSnackbar("Привязка типа атрибута успешно обновлена", {
                variant: "success",
            });
            onClose();
            dispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении привязки типа атрибута", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    return (
        <>
            <DialogContent>
                {loadingStatus === "loading" && <ModalContentLoading />}

                {(loadingStatus === "inactivity" || loadingStatus === "loaded") && (
                    <AttributeTypeBindForm
                        ref={formRef}
                        data={data}
                        onSubmit={
                            data ? handleUpdateAttributeTypeBind : handleCreateAttributeTypeBind
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="default" onClick={onClose}>
                    Отмена
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={invokeSubmit}
                    disabled={submitLoadingStatus === "loading"}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </>
    );
};

export default AttributeTypeBindDialogContext;
